import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { IconCollection } from "components/Icon/IconCollection";
import { ResourceConfig } from "config/config";
import {
    scriptField,
    valueAndSchemaInputField,
    valueAndSchemaOutputField,
} from "config/resources/sharedFieldsTyped";
import {
    DeleteListAction,
    MigrateDeprecationsListAction,
    PermanentlyDeleteListAction,
    deleteActionSignature,
    deletePermanentlyActionSignature,
    downloadActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    migrateDeprecationsActionSignature,
    moveToLocationActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    configurationSchemaTab,
    configurationSchemaTabKey,
    logsTab,
    metadataTab,
    outputSchemaTab,
    schedulerInputSchemaTab,
    usedByTab,
    valueAndSchemaInput,
    valueAndSchemaOutput,
    wrappersTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { executionPriorityColumn } from "containers/Search/components/SearchTabs/columns";
import resource from "./resource";
import { getLatestExecutions } from "./sharedFields";

export const schedulerStatsFieldName = "schedule_id--schedule.id:scheduler";

const resourceName = "scheduler";

const config: ResourceConfig = {
    label: "Scheduler",
    icon: IconCollection.fileClockOutline,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: configurationSchemaTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [
                    configurationSchemaTab.tab,
                    schedulerInputSchemaTab.tab,
                    outputSchemaTab.tab,
                    wrappersTab.tab,
                    usedByTab.tab,
                    logsTab.tab,
                    metadataTab.tab,
                    commentsTab.tab,
                ],
                content: [
                    configurationSchemaTab.content,
                    schedulerInputSchemaTab.content,
                    outputSchemaTab.content,
                    wrappersTab.content,
                    usedByTab.content,
                    logsTab.content,
                    metadataTab.content,
                    commentsTab.content,
                ],
            },
        },
    ],
    actions: {
        shown: [migrateDeprecationsActionSignature],
        hidden: [
            duplicateActionSignature,
            downloadActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
            { key: "migrateDeprecations", action: MigrateDeprecationsListAction },
        ],
    },
    additionalColumns: (setNewValue) => [executionPriorityColumn(setNewValue)],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            script
            configuration_schema
            output_schema
            breakpoints
            priority
            has_deprecation
            has_syntax_error
            max_runtime_minutes
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            has_deprecation
            has_syntax_error
            priority
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $script: String,
            $configuration_schema: SchemalessData,
            $output_schema: SchemalessData,
            $breakpoints: [Int],
            $priority: Int
            $max_runtime_minutes: Int
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            script: $script,
            configuration_schema: $configuration_schema,
            output_schema: $output_schema,
            breakpoints: $breakpoints,
            priority: $priority
            max_runtime_minutes: $max_runtime_minutes
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $script: String
            $configuration_schema: SchemalessData,
            $output_schema: SchemalessData,
            $breakpoints: [Int],
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            script: $script
            configuration_schema: $configuration_schema,
            output_schema: $output_schema,
            breakpoints: $breakpoints
        `,
    },
    statsSidebar: [
        {
            ...getLatestExecutions("scheduler"),
            resourceNameQueryField: schedulerStatsFieldName,
        },
    ],
    form: [
        scriptField,
        valueAndSchemaInputField("configuration_schema"),
        valueAndSchemaOutputField,
    ],
    formDTO: {
        initialValueDTO: (data: any) => {
            const valueAndSchemaInputFieldValues: SchematizedValues = {
                values: data?.input_value,
                schema: data?.input_schema,
            };
            const valueAndSchemaOutputFieldValues: SchematizedValues = {
                values: data?.output_value,
                schema: data?.output_schema,
            };
            return {
                ...data,
                [valueAndSchemaInput]: valueAndSchemaInputFieldValues,
                [valueAndSchemaOutput]: valueAndSchemaOutputFieldValues,
            };
        },
    },
};
export default config;
